// src/adapters/profile/selectors.ts
import { computeLensRank } from '../../utils/rankLens';

export type UiHeaderModel = {
  name: string;
  valueScoreRaw: number;
  easeScoreRaw: number;
  totalScoreRaw: number;
  global7030Rank: number | null;
  lensRank: number | null;
  displayMode: 'raw' | 'calibrated';
  updatedAt?: string | null;
};

export function selectHeader(
  raw: any, 
  opts: { 
    lens: 'quickwins' | '70_30' | '50_50', 
    displayMode: 'raw' | 'calibrated' 
  }
): UiHeaderModel {
  // Handle the scores from the API payload
  const scores = raw?.scores || raw || {}; // Flexible to handle different payload shapes
  
  // Extract raw scores from client_use_case_scores
  const value = scores.value_score ?? null;
  const ease = scores.implementation_score ?? null;
  const total = scores.total_score ?? null;
  const gRank = scores.rank ?? null;
  
  // Compute lens rank if we have all required scores
  const lensRank = (value !== null && ease !== null && total !== null)
    ? computeLensRank({ 
        lens: opts.lens, 
        valueScore: value, 
        easeScore: ease, 
        totalScore: total 
      })
    : null;
  
  return {
    name: raw?.name || raw?.use_case?.name || '',
    valueScoreRaw: value,
    easeScoreRaw: ease,
    totalScoreRaw: total,
    global7030Rank: gRank,
    lensRank,
    displayMode: opts.displayMode,
    updatedAt: scores.updated_at ?? null
  };
}

export type ROIModel = {
  roiPercentage: number | null;
  paybackMonths: number | null;
  units: { roi: string; payback: string };
  updatedAt: string | null;
  source: 'assumptions' | 'calculations' | null;
};

export function selectROI(
  assumptions: any[],
  calculations: any[]
): ROIModel {
  let roiValue = null;
  let paybackValue = null;
  let roiUnit = '%';
  let paybackUnit = 'months';
  let updatedAt = null;
  let source = null;

  // First, try to find ROI from calculations (priority)
  if (calculations && calculations.length > 0) {
    // Sort by step descending to get latest/highest step
    const sortedCalcs = [...calculations].sort((a, b) => 
      (b.step || 0) - (a.step || 0)
    );

    // Look for ROI - check both formula and step 3 specifically
    for (const calc of sortedCalcs) {
      const formulaLower = (calc.formula || '').toLowerCase();
      
      // Check if this is an ROI calculation
      if (formulaLower.includes('roi') && 
          !formulaLower.includes('payback')) {
        roiValue = calc.result;
        updatedAt = calc.updated_at?.value || calc.updated_at || updatedAt;
        source = 'calculations';
        
        // For step 3, the result is already a percentage (79 means 79%)
        if (calc.step === 3) {
          roiUnit = '%';
        }
        break;
      }
    }

    // Look for payback
    for (const calc of sortedCalcs) {
      const formulaLower = (calc.formula || '').toLowerCase();
      
      if (formulaLower.includes('payback')) {
        // Step 5 shows 0.15 which needs to be converted to months
        paybackValue = calc.result;
        
        // If the value is very small (< 1), it's likely in years, convert to months
        if (paybackValue < 1) {
          paybackValue = Math.round(paybackValue * 12);
        }
        
        updatedAt = calc.updated_at?.value || calc.updated_at || updatedAt;
        break;
      }
    }
  }

  // Fallback to assumptions if needed
  if (!roiValue && assumptions) {
    const roiAssumption = assumptions.find(a => 
      a.label?.toLowerCase().includes('roi')
    );
    if (roiAssumption) {
      roiValue = roiAssumption.value;
      roiUnit = roiAssumption.unit || '%';
      updatedAt = roiAssumption.updated_at?.value || roiAssumption.updated_at || updatedAt;
      source = source || 'assumptions';
    }
  }

  if (!paybackValue && assumptions) {
    const paybackAssumption = assumptions.find(a => 
      a.label?.toLowerCase().includes('payback')
    );
    if (paybackAssumption) {
      paybackValue = paybackAssumption.value;
      paybackUnit = paybackAssumption.unit || 'months';
      updatedAt = paybackAssumption.updated_at?.value || paybackAssumption.updated_at || updatedAt;
    }
  }

  return {
    roiPercentage: roiValue,
    paybackMonths: paybackValue,
    units: { roi: roiUnit, payback: paybackUnit },
    updatedAt,
    source
  };
}

export type NarrativesModel = {
  marketProblem: string;
  productSolution: string;
  businessValue: string;
  source: 'client' | 'base';
  updatedAt?: string | null;
};

export function selectNarratives(
  base: any,
  client: any[],
  sectionMap: Record<string, string>
): NarrativesModel {
  // Section IDs we're looking for
  const marketProblemId = sectionMap?.['market_problem'] || 'market_problem';
  const productSolutionId = sectionMap?.['product_solution'] || 'product_solution';
  const businessValueId = sectionMap?.['business_value'] || 'business_value';

  let marketProblem = '';
  let productSolution = '';
  let businessValue = '';
  let source: 'client' | 'base' = 'base';
  let updatedAt = null;

  // Check client sections first (precedence)
  if (client && client.length > 0) {
    const clientMarket = client.find(s => s.section_id === marketProblemId);
    const clientProduct = client.find(s => s.section_id === productSolutionId);
    const clientBusiness = client.find(s => s.section_id === businessValueId);

    if (clientMarket || clientProduct || clientBusiness) {
      source = 'client';
      marketProblem = clientMarket?.content || '';
      productSolution = clientProduct?.content || '';
      businessValue = clientBusiness?.content || '';
      
      // Get latest updated_at
      const dates = [
        clientMarket?.updated_at?.value || clientMarket?.updated_at,
        clientProduct?.updated_at?.value || clientProduct?.updated_at,
        clientBusiness?.updated_at?.value || clientBusiness?.updated_at
      ].filter(Boolean);
      
      if (dates.length > 0) {
        updatedAt = dates.sort().reverse()[0];
      }
    }
  }

  // Fallback to base if no client data
  if (!marketProblem && !productSolution && !businessValue && base) {
    // Try different possible field names in base
    marketProblem = base.market_problem || base.marketProblem || '';
    productSolution = base.product_solution || base.productSolution || '';
    businessValue = base.business_value || base.businessValue || '';
    source = 'base';
    updatedAt = base.updated_at?.value || base.updated_at || null;
  }

  return {
    marketProblem,
    productSolution,
    businessValue,
    source,
    updatedAt
  };
}

export type RecommendationModel = {
  status: 'Recommended' | 'Consider' | 'Monitor' | 'Defer';
  rationale: string;
  nextSteps: string[];
  source: 'client' | 'base';
  updatedAt?: string | null;
};

export function selectRecommendation(
  base: any,
  client: any[],
  sectionMap: Record<string, string>
): RecommendationModel {
  // Section IDs we're looking for
  const recommendationId = sectionMap?.['recommendation'] || 'recommendation';
  const rationaleId = sectionMap?.['rationale'] || 'rationale';
  const nextStepsId = sectionMap?.['next_steps'] || 'next_steps';

  let status: RecommendationModel['status'] = 'Consider';
  let rationale = '';
  let nextSteps: string[] = [];
  let source: 'client' | 'base' = 'base';
  let updatedAt = null;

  // Check client sections first (precedence)
  if (client && client.length > 0) {
    const clientRec = client.find(s => 
      s.section_id === recommendationId || 
      s.section_id === 'recommendation_status'
    );
    const clientRationale = client.find(s => 
      s.section_id === rationaleId || 
      s.section_id === 'strategic_rationale'
    );
    const clientNextSteps = client.find(s => 
      s.section_id === nextStepsId || 
      s.section_id === 'immediate_next_steps'
    );

    if (clientRec || clientRationale || clientNextSteps) {
      source = 'client';
      
      // Extract status
      if (clientRec?.content) {
        const content = clientRec.content.toLowerCase();
        if (content.includes('recommended')) status = 'Recommended';
        else if (content.includes('consider')) status = 'Consider';
        else if (content.includes('monitor')) status = 'Monitor';
        else if (content.includes('defer')) status = 'Defer';
      }
      
      // Extract rationale
      rationale = clientRationale?.content || '';
      
      // Extract next steps (could be JSON array or string)
      if (clientNextSteps?.content) {
        try {
          const parsed = JSON.parse(clientNextSteps.content);
          nextSteps = Array.isArray(parsed) ? parsed : [clientNextSteps.content];
        } catch {
          // If not JSON, treat as string and split by newlines or bullets
          nextSteps = clientNextSteps.content
            .split(/[\n•·-]/)
            .map(s => s.trim())
            .filter(Boolean);
        }
      }
      
      // Get latest updated_at
      const dates = [
        clientRec?.updated_at?.value || clientRec?.updated_at,
        clientRationale?.updated_at?.value || clientRationale?.updated_at,
        clientNextSteps?.updated_at?.value || clientNextSteps?.updated_at
      ].filter(Boolean);
      
      if (dates.length > 0) {
        updatedAt = dates.sort().reverse()[0];
      }
    }
  }

  // Fallback to base if no client data
  if (!rationale && !nextSteps.length && base) {
    // Extract status from base
    if (base.recommendation_status || base.status) {
      const baseStatus = (base.recommendation_status || base.status || '').toLowerCase();
      if (baseStatus.includes('recommended')) status = 'Recommended';
      else if (baseStatus.includes('consider')) status = 'Consider';
      else if (baseStatus.includes('monitor')) status = 'Monitor';
      else if (baseStatus.includes('defer')) status = 'Defer';
    }
    
    // Extract rationale
    rationale = base.strategic_rationale || base.rationale || base.recommendation_rationale || '';
    
    // Extract next steps
    if (base.immediate_next_steps || base.next_steps) {
      const steps = base.immediate_next_steps || base.next_steps;
      if (Array.isArray(steps)) {
        nextSteps = steps;
      } else if (typeof steps === 'string') {
        try {
          const parsed = JSON.parse(steps);
          nextSteps = Array.isArray(parsed) ? parsed : [steps];
        } catch {
          nextSteps = steps
            .split(/[\n•·-]/)
            .map(s => s.trim())
            .filter(Boolean);
        }
      }
    }
    
    source = 'base';
    updatedAt = base.updated_at?.value || base.updated_at || null;
  }

  return {
    status,
    rationale,
    nextSteps,
    source,
    updatedAt
  };
}